import { create } from 'zustand';
import { getMeTimeClock } from '@/api/generated/me-time-clock/me-time-clock';
import type { FacilityFlowCoreModulesPlanningWorkflowsTimeClockStampingWindowDto } from '@/api/generated/models';

export type StampingWindow = FacilityFlowCoreModulesPlanningWorkflowsTimeClockStampingWindowDto;

const api = getMeTimeClock();

/** A failed load is retried after this long (on the next minute tick or window focus). */
const RETRY_AFTER_MS = 60_000;

/** Cache key: the window belongs to one user and one local date. */
export function stampingWindowKey(employeeId: string | null | undefined, date: string): string {
  return `${employeeId ?? 'anonymous'}|${date}`;
}

interface StampingWindowState {
  /** Effective clock-in window per `stampingWindowKey(employeeId, date)`. */
  windows: Record<string, StampingWindow>;
  /** When loading a key last failed — the caller falls back to the tenant policy meanwhile. */
  failedAt: Record<string, number>;
  /** Loads the window of the signed-in user for `date` once; concurrent callers share it. */
  fetchWindow: (employeeId: string | null | undefined, date: string) => Promise<void>;
  /** Drops every cached window — called on logout. */
  reset: () => void;
}

// why: header widget, dashboard and time clock page all ask for the same date on mount.
const inFlight = new Map<string, Promise<void>>();

/**
 * The own clock-in window from `GET /me/timeclock/window` (FACF-149): the season of the user's
 * time clock group, or the tenant policy when no group period covers the date. Keyed by the
 * employee, so a different user signing in (without a page reload) never sees a cached window.
 */
export const useStampingWindowStore = create<StampingWindowState>((set, get) => ({
  windows: {},
  failedAt: {},

  reset: () => {
    inFlight.clear();
    set({ windows: {}, failedAt: {} });
  },

  fetchWindow: async (employeeId, date) => {
    const key = stampingWindowKey(employeeId, date);
    if (get().windows[key]) return;
    const failedAt = get().failedAt[key];
    if (failedAt && Date.now() - failedAt < RETRY_AFTER_MS) return;
    const running = inFlight.get(key);
    if (running) return running;
    const request = (async () => {
      try {
        const window = await api.getApiV1MeTimeclockWindow({ date });
        set((s) => {
          const failed = { ...s.failedAt };
          delete failed[key];
          return { windows: { ...s.windows, [key]: window }, failedAt: failed };
        });
      } catch (err) {
        console.error('[StampingWindowStore] fetch failed', err);
        set((s) => ({ failedAt: { ...s.failedAt, [key]: Date.now() } }));
      } finally {
        inFlight.delete(key);
      }
    })();
    inFlight.set(key, request);
    return request;
  },
}));
